/**
 * How a paper draws its tables (design-handoffs/2026-10-05-document-themes,
 * 2a and 2f). Pure: the Look sample, the table tool's Preview as and the
 * tables list's detail all draw from this.
 *
 * The paper may touch only the header row's face and weight, the top rule's
 * colour, the caption label's colour and face, the body face, and the paper's
 * table style (ruled, banded, header band; density; caption position). Never
 * content, widths, alignment, number format or the caption text.
 */
import type { CaptionPosition, LookPaper, TableDensity, TableStyle, ThemeDescriptor } from "@/lib/api";
import { contrastRatio, type ResolvedTables } from "./look";
import { themeFontStack } from "./fonts";

/** Ink on a fill that fails white: charcoal. */
export const CHARCOAL = "#1A1A1A";
const WHITE = "#FFFFFF";
const HEX6 = /^#[0-9A-Fa-f]{6}$/;

/** White where it holds 4.5:1 on the fill, charcoal otherwise (Gazette yellow, Exposition gold). */
export function inkOn(fill: string): string {
  return contrastRatio(fill, WHITE) >= 4.5 ? WHITE : CHARCOAL;
}

/** `hue` laid over `ground` at `amount` (0–1), as `#RRGGBB`. */
export function tint(hue: string, ground: string, amount: number): string {
  if (!HEX6.test(hue) || !HEX6.test(ground)) return ground;
  const a = parseInt(hue.slice(1), 16);
  const b = parseInt(ground.slice(1), 16);
  const mix = (shift: number) => {
    const x = (a >> shift) & 255;
    const y = (b >> shift) & 255;
    return Math.round(y + (x - y) * amount);
  };
  return `#${[16, 8, 0].map((s) => mix(s).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

/** Banding switches itself off below four body rows, where stripes are just noise. */
export const BANDED_MIN_ROWS = 4;

/** Stripes are tints, not colours: 7 % of the theme hue. */
export const BAND_AMOUNT = 0.07;

/** Which body rows a banded table tints: the 2nd, 4th… and none below four rows. */
export function bandedRow(index: number, bodyRows: number): boolean {
  return bodyRows >= BANDED_MIN_ROWS && index % 2 === 1;
}

/** Everything a client-drawn table needs to look like its paper. */
export interface PaperTableStyle {
  themeId: ThemeDescriptor["id"];
  style: TableStyle;
  density: TableDensity;
  caption: CaptionPosition;
  /** The page the table sits on, and its ink. */
  paper: string;
  ink: string;
  headFont: string;
  /** Null: the header keeps the plain preview's weight (Classic). */
  headWeight: number | null;
  bodyFont: string;
  /** The top rule. Null under a header band, which has none. */
  rule: string | null;
  /** The caption label ("Table 3.1") colour and face. */
  label: string;
  labelFont: string;
  /** Header band only: the fill and the ink on it. */
  headFill: string | null;
  headInk: string;
  /** Banded only: the stripe colour. */
  band: string | null;
}

/**
 * The look a paper gives its tables. `sectionColour` is the chapter's colour
 * when the paper uses Index: the rule, the label and the header band take it.
 */
export function paperTableStyle(
  theme: ThemeDescriptor,
  tables: ResolvedTables,
  opts: { sectionColour?: string | null; paper?: LookPaper } = {},
): PaperTableStyle {
  const white = opts.paper === "white";
  const paper = white ? theme.printSafe.paper : theme.colours.paper;
  const ink = white ? theme.printSafe.ink : theme.colours.ink;
  const section = opts.sectionColour && HEX6.test(opts.sectionColour) ? opts.sectionColour : null;
  const hue = section ?? theme.colours.accent;
  // The server's palette, when it sends one, is what the .sty prints: Gazette's band is white on
  // its yellow paper (a yellow band would vanish there) and yellow with charcoal ink on white.
  const palette = theme.table ? (white ? theme.table.onWhite : theme.table.onPaper) : null;
  const resolve = (v: string) => (v !== "chapter" && HEX6.test(v) ? v : hue);
  const bandHue = palette ? resolve(palette.hue) : hue;
  const bandAmount = palette && palette.band > 0 ? palette.band / 100 : BAND_AMOUNT;
  const classic = theme.id === "classic";
  const headFont = themeFontStack(theme.fonts.heading, theme.fonts.headingCss);
  const header = tables.style === "header";
  const headFill = header ? (palette ? resolve(palette.head) : hue) : null;
  const headInk = !headFill
    ? ink
    : palette && palette.head !== "chapter" && HEX6.test(palette.headInk)
      ? palette.headInk
      : inkOn(headFill);
  return {
    themeId: theme.id,
    style: tables.style,
    density: tables.density,
    caption: tables.caption,
    paper,
    ink,
    headFont,
    headWeight: header ? 700 : classic ? null : 700,
    bodyFont: themeFontStack(theme.fonts.body, theme.fonts.bodyCss),
    rule: header ? null : hue,
    label: section ?? theme.colours.heading,
    labelFont: headFont,
    headFill,
    headInk,
    band: tables.style === "banded" ? tint(bandHue, paper, bandAmount) : null,
  };
}

/** Row and type sizes for a density: Compact is \small with tighter rows. */
export function densityScale(density: TableDensity): { font: number; row: number; padX: number } {
  return density === "compact" ? { font: 0.88, row: 0.8, padX: 0.6 } : { font: 1, row: 1, padX: 1 };
}
